/**
 * Progress Components
 * 
 * Circular and linear progress indicators
 * 
 * Version: 6.2.0
 */
import React from 'react'
import './Progress.css'

interface CircularProgressProps {
  value: number
  size?: number
  strokeWidth?: number
  showValue?: boolean
  label?: string
  color?: string
  trackColor?: string
  className?: string
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  value,
  size = 120,
  strokeWidth = 8,
  showValue = true,
  label,
  color = 'var(--primary)',
  trackColor = 'var(--bg-secondary)',
  className = ''
}) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const progress = Math.min(Math.max(value, 0), 100)
  const offset = circumference - (progress / 100) * circumference

  return (
    <div className={`circular-progress ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="progress-arc"
        />
      </svg>
      
      {showValue && (
        <div className="circular-progress-content">
          <span className="progress-value">{Math.round(progress)}%</span>
          {label && <span className="progress-label">{label}</span>}
        </div>
      )}
    </div>
  )
}

interface LinearProgressProps {
  value: number
  height?: number
  showValue?: boolean
  label?: string
  color?: string
  trackColor?: string
  variant?: 'default' | 'striped' | 'gradient'
  className?: string
}

export const LinearProgress: React.FC<LinearProgressProps> = ({
  value,
  height = 8,
  showValue = false,
  label,
  color = 'var(--primary)',
  trackColor = 'var(--bg-secondary)',
  variant = 'default',
  className = ''
}) => {
  const progress = Math.min(Math.max(value, 0), 100)

  return (
    <div className={`linear-progress ${className}`}>
      {(showValue || label) && (
        <div className="linear-progress-header">
          {label && <span className="progress-label">{label}</span>}
          {showValue && <span className="progress-value">{Math.round(progress)}%</span>}
        </div>
      )}
      <div className="linear-progress-track" style={{ height, backgroundColor: trackColor }}>
        <div 
          className={`linear-progress-fill variant-${variant}`}
          style={{ 
            width: `${progress}%`, 
            backgroundColor: variant === 'gradient' ? undefined : color 
          }}
        >
          {variant === 'gradient' && <div className="gradient-shimmer" />}
        </div>
      </div>
    </div>
  )
}

interface ProgressRingProps {
  children?: React.ReactNode
  size?: number
  progress: number
  color?: string
  trackColor?: string
  strokeWidth?: number
  className?: string
}

export const ProgressRing: React.FC<ProgressRingProps> = ({
  children,
  size = 100,
  progress,
  color = 'var(--primary)',
  trackColor = 'var(--bg-secondary)',
  strokeWidth = 6,
  className = ''
}) => {
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (progress / 100) * circumference

  return (
    <div className={`progress-ring ${className}`} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <div className="progress-ring-content">
        {children}
      </div>
    </div>
  )
}

interface MultiProgressProps {
  items: { label: string; value: number; color: string }[]
  total?: number
  className?: string
}

export const MultiProgress: React.FC<MultiProgressProps> = ({
  items,
  total,
  className = ''
}) => {
  const calculatedTotal = total || items.reduce((sum, item) => sum + item.value, 0)

  return (
    <div className={`multi-progress ${className}`}>
      <div className="multi-progress-bar">
        {items.map((item, index) => (
          <div
            key={index}
            className="multi-progress-segment"
            style={{
              width: `${(item.value / calculatedTotal) * 100}%`,
              backgroundColor: item.color
            }}
          />
        ))}
      </div>
      <div className="multi-progress-legend">
        {items.map((item, index) => (
          <div key={index} className="legend-item">
            <span className="legend-color" style={{ backgroundColor: item.color }} />
            <span className="legend-label">{item.label}</span>
            <span className="legend-value">{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default CircularProgress